import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, Map, Menu, LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";

import logo from "@/assets/logo-jobteen.png";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import LiquidGlassNav from "./LiquidGlassNav";


interface NavLink {
  href: string;
  label: string;
  icon?: LucideIcon;
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, signOut, loading } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);



  // Students don't see "Accueil" - they see "Offres d'emploi" as their main nav item
  const navLinks: NavLink[] = role === "student" 
    ? [
        { href: "/offres", label: "Offres d'emploi" },
        { href: "/carte", label: "Carte", icon: Map },
        { href: "/a-propos", label: "À propos" },
        { href: "/contact", label: "Contact" },
      ]
    : [
        { href: "/", label: "Accueil" },
        { href: "/offres", label: "Offres d'emploi" },
        { href: "/carte", label: "Carte", icon: Map },
        { href: "/a-propos", label: "À propos" },
        { href: "/contact", label: "Contact" },
      ];

  const isActive = (path: string) => location.pathname === path;

  const handleSignOut = async () => {
    setIsMenuOpen(false);
    const { error } = await signOut();
    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible de se déconnecter. Veuillez réessayer.",
        variant: "destructive",
      });
      return;
    }
    navigate("/", { replace: true });
  };

  const dashboardLink = role === "student" ? "/espace-etudiant" : role === "employer" ? "/espace-recruteur" : null;
  
  // Logo redirects to /offres when logged in as student, otherwise home
  const logoLink = role === "student" ? "/offres" : "/";

  return (
    <header
      className={`sticky top-0 z-[1100] w-full transition-all duration-300 ease-soft ${
        scrolled
          ? "border-b border-border/70 bg-background/75 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 shadow-xs"
          : "border-b border-transparent bg-background/95 backdrop-blur-md"
      }`}
    >
      <div
        className={`container flex items-center justify-between transition-[height] duration-300 ease-soft ${
          scrolled ? "h-[72px] md:h-20" : "h-24 md:h-28"
        }`}
      >
        <Link to={logoLink} className="flex items-center gap-2 group shrink-0">
          <img
            src={logo}
            alt="Logo JobTeen – Plateforme d'emploi pour les jeunes au Luxembourg"
            className={`w-auto transition-all duration-300 ease-soft group-hover:opacity-80 ${
              scrolled ? "h-9 md:h-11" : "h-11 md:h-12"
            }`}
            width={281}
            height={72}
          />
        </Link>

        {/* Desktop Navigation — liquid glass */}
        <LiquidGlassNav links={navLinks} />



        {/* Desktop Auth Buttons */}
        <div className="hidden md:flex items-center gap-3">
          {!loading && user ? (
            <>
              {dashboardLink && (
                <Button asChild variant="ghost" size="sm">
                  <Link to={dashboardLink}>Mon espace</Link>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleSignOut} className="gap-2">
                <LogOut className="h-4 w-4" />
                Déconnexion
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/connexion">Connexion</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/inscription">S'inscrire</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu - Sheet Drawer */}
        <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]" aria-label="Menu">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full max-w-sm">
            <SheetHeader className="text-left mb-6">
              <SheetTitle>
                <img src={logo} alt="JobTeen" className="h-10 w-auto" />
              </SheetTitle>
            </SheetHeader>
            
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`min-h-[48px] flex items-center gap-2 px-4 rounded-lg text-base font-medium transition-[background-color,color,transform] duration-200 ease-soft active:scale-[0.98] hover:bg-muted ${
                    isActive(link.href) ? "text-primary bg-primary/10" : "text-foreground"
                  }`}
                >
                  {link.icon && <link.icon className="h-5 w-5" />}
                  {link.label}
                </Link>
              ))}
            </nav>
            
            <div className="flex flex-col gap-3 pt-6 mt-6 border-t border-border">
              {user ? (
                <>
                  {dashboardLink && (
                    <Button asChild variant="outline" className="w-full min-h-[48px] justify-start">
                      <Link to={dashboardLink} onClick={() => setIsMenuOpen(false)}>
                        Mon espace
                      </Link>
                    </Button>
                  )}
                  <Button 
                    variant="destructive" 
                    className="w-full min-h-[48px] justify-start gap-2" 
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-4 w-4" />
                    Déconnexion
                  </Button>
                </>
              ) : (
                <>
                  <Button asChild variant="outline" className="w-full min-h-[48px]">
                    <Link to="/connexion" onClick={() => setIsMenuOpen(false)}>
                      Connexion
                    </Link>
                  </Button>
                  <Button asChild className="w-full min-h-[48px]">
                    <Link to="/inscription" onClick={() => setIsMenuOpen(false)}>
                      S'inscrire
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Header;